import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, CheckCircle2, ShieldCheck, FileText, TrendingUp,
  Hammer, Building2, Scissors, ShoppingBag, Landmark,
} from "lucide-react";
import { MaskedLines, FadeUp, Overline } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import CompareSlider from "@/components/CompareSlider";
import CtaBand from "@/components/CtaBand";
import { LogoMark } from "@/components/Logo";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/f38635fe-5ba6-4f07-bccf-ef12c441b66a/images/b95b14a668c8bdece7fbc980fbc0aa1c43e03fd20586e2593a828506025bc080.jpeg";

const SERVICES = [
  {
    icon: FileText,
    title: "Keep the Books Clean",
    items: ["Monthly bookkeeping", "Reconciliation", "Transaction categorization", "Financial reporting", "Catch-up & cleanup bookkeeping"],
    testid: "services-card-bookkeeping",
  },
  {
    icon: ShieldCheck,
    title: "Keep the Business Compliant",
    items: ["Tax preparation", "Estimated tax planning", "Pennsylvania sales & use tax filing", "Payroll support", "Related filings"],
    testid: "services-card-compliance",
  },
  {
    icon: TrendingUp,
    title: "Understand the Numbers",
    items: ["Profit & loss and balance sheet review", "Job costing", "QuickBooks setup and support", "Practical help understanding your financials"],
    testid: "services-card-advisory",
  },
];

const INDUSTRIES = [
  { icon: Hammer, title: "Trades & Construction", sub: "Construction · Landscaping · Plumbing · Electrical · HVAC" },
  { icon: Building2, title: "Real Estate & Property", sub: "Rental Properties · Property Management" },
  { icon: Scissors, title: "Professional & Service", sub: "Salons · IT / Technology · Senior Transition · Biotech" },
  { icon: ShoppingBag, title: "Retail & Product-Based", sub: "Screen Printing · Retail · Food Trucks · Car Lots" },
  { icon: Landmark, title: "Other Industries", sub: "Churches & Nonprofits · Manufacturing · Trucking & Logistics" },
];

const FACTORS = [
  "Monthly transaction volume", "Bank & credit card accounts", "Condition of the books",
  "Payroll", "Sales tax requirements", "Accounts receivable", "Job costing", "Reporting complexity",
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const pillY = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <div>
      {/* HERO */}
      <section ref={heroRef} className="ledger-lines relative overflow-hidden pb-20 pt-32 lg:pb-28 lg:pt-40" data-testid="home-hero">
        <div className="pointer-events-none absolute inset-y-0 left-8 hidden w-px bg-gold/40 lg:block" aria-hidden="true" />
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <FadeUp>
              <Overline>Bookkeeping &amp; Tax · Wellsville &amp; Central PA</Overline>
            </FadeUp>
            <MaskedLines
              as="h1"
              delay={0.15}
              lines={["Know How Your", "Business Is", "Really Doing."]}
              className="mt-6 font-serif text-5xl font-medium leading-[1.04] tracking-tight text-ink sm:text-6xl lg:text-7xl"
            />
            <FadeUp delay={0.5}>
              <p className="mt-8 max-w-xl text-lg leading-relaxed text-stone2">
                Arnold Bookkeeping helps small-business owners keep accurate books, understand
                their finances, and stay ahead of tax and reporting obligations — without having
                to become accountants themselves.
              </p>
            </FadeUp>
            <FadeUp delay={0.62}>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to="/contact"
                  data-testid="hero-cta-get-started"
                  className="inline-flex items-center gap-2 rounded-md bg-pine px-7 py-4 text-sm font-semibold text-cream transition-colors duration-200 hover:bg-gold hover:text-ink"
                >
                  Get Started <ArrowUpRight size={16} />
                </Link>
                <Link
                  to="/services"
                  data-testid="hero-cta-services"
                  className="group inline-flex items-center gap-2 rounded-md border border-pine/25 px-7 py-4 text-sm font-semibold text-pine transition-colors duration-200 hover:border-pine"
                >
                  Explore Services
                  <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </div>
            </FadeUp>
          </div>

          <motion.div style={{ y: imgY }} className="relative lg:col-span-5">
            <div className="absolute -inset-3 rotate-2 rounded-2xl border border-gold/50" aria-hidden="true" />
            <img
              src={HERO_IMG}
              alt="Organized bookkeeping desk with calculator and ledger"
              className="relative aspect-[4/5] w-full rounded-2xl object-cover shadow-2xl"
              data-testid="home-hero-image"
            />
            <motion.div style={{ y: pillY }} className="absolute inset-0">
              <div className="absolute -right-3 top-8 flex animate-float-slow items-center gap-2.5 rounded-xl border border-hairline bg-white/80 px-4 py-3 shadow-lg backdrop-blur-md sm:-right-6">
                <CheckCircle2 size={18} className="text-gold" />
                <span className="text-xs font-semibold text-ink">Books reconciled, every month</span>
              </div>
              <div className="absolute -left-3 bottom-10 flex animate-float-slow items-center gap-2.5 rounded-xl border border-hairline bg-white/80 px-4 py-3 shadow-lg backdrop-blur-md sm:-left-8" style={{ animationDelay: "1.4s" }}>
                <ShieldCheck size={18} className="text-gold" />
                <span className="text-xs font-semibold text-ink">Tax-ready, year-round</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* SERVICES */}
      <section className="py-24 lg:py-32" data-testid="home-services-section">
        <div className="mx-auto max-w-7xl px-6">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <FadeUp>
                <Overline>What we do</Overline>
              </FadeUp>
              <MaskedLines
                as="h2"
                inView
                lines={["Three ways we make the", "numbers easier."]}
                className="mt-5 font-serif text-4xl font-medium leading-[1.08] tracking-tight text-ink sm:text-5xl"
              />
            </div>
            <FadeUp delay={0.2}>
              <Link
                to="/services"
                data-testid="home-services-view-all"
                className="group inline-flex items-center gap-2 text-sm font-semibold text-pine"
              >
                All services
                <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
            </FadeUp>
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {SERVICES.map((s, i) => (
              <FadeUp key={s.title} delay={i * 0.1}>
                <Link
                  to="/services"
                  data-testid={s.testid}
                  className="group flex h-full flex-col rounded-2xl border border-hairline bg-white p-8 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-parchment text-pine transition-colors duration-300 group-hover:bg-pine group-hover:text-gold">
                    <s.icon size={22} />
                  </span>
                  <h3 className="mt-6 font-serif text-2xl font-semibold text-ink">{s.title}</h3>
                  <ul className="mt-5 flex-1 space-y-2.5">
                    {s.items.map((it) => (
                      <li key={it} className="flex items-start gap-2.5 text-sm text-stone2">
                        <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-gold" />
                        {it}
                      </li>
                    ))}
                  </ul>
                  <span className="mt-7 inline-flex items-center gap-1.5 text-sm font-semibold text-pine">
                    Learn more
                    <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
                  </span>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* SHOEBOX VS CLEAN */}
      <section className="bg-parchment py-24 lg:py-32" data-testid="home-compare-section">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <FadeUp>
              <Overline>The difference</Overline>
            </FadeUp>
            <MaskedLines
              as="h2"
              inView
              lines={["From shoebox", "to clear."]}
              className="mt-5 font-serif text-4xl font-medium leading-[1.08] tracking-tight text-ink sm:text-5xl"
            />
            <FadeUp delay={0.2}>
              <p className="mt-6 leading-relaxed text-stone2">
                For some businesses, bookkeeping is primarily about keeping accurate records. For
                others, those records help answer much bigger questions — can I afford another
                employee, is this job actually profitable, how much should I set aside for taxes?
              </p>
              <p className="mt-4 leading-relaxed text-stone2">
                Good bookkeeping should give you confidence in the numbers you use to run your
                business.
              </p>
            </FadeUp>
            <FadeUp delay={0.3}>
              <Link
                to="/pricing"
                data-testid="home-compare-pricing-link"
                className="mt-8 inline-flex items-center gap-2 rounded-md bg-pine px-6 py-3.5 text-sm font-semibold text-cream transition-colors duration-200 hover:bg-gold hover:text-ink"
              >
                What does bookkeeping cost in 2026? <ArrowRight size={15} />
              </Link>
            </FadeUp>
          </div>
          <div className="lg:col-span-7">
            <FadeUp delay={0.15}>
              <CompareSlider
                before="https://images.unsplash.com/photo-1649209979970-f01d950cc5ed?q=80&w=1600&auto=format&fit=crop"
                after="https://static.prod-images.emergentagent.com/jobs/f38635fe-5ba6-4f07-bccf-ef12c441b66a/images/5fb481ea9c5540d65094e4bf8a77f41bb28b858527e819bf12b72aed0cd4a238.jpeg"
                beforeLabel="The shoebox"
                afterLabel="The Arnold system"
              />
            </FadeUp>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-24 lg:py-32" data-testid="home-industries-section">
        <div className="mx-auto max-w-7xl px-6">
          <FadeUp>
            <Overline>Who we serve</Overline>
          </FadeUp>
          <MaskedLines
            as="h2"
            inView
            lines={["Industries & businesses", "we understand."]}
            className="mt-5 font-serif text-4xl font-medium leading-[1.08] tracking-tight text-ink sm:text-5xl"
          />
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {INDUSTRIES.map((ind, i) => (
              <FadeUp key={ind.title} delay={i * 0.07} className="h-full">
                <Link
                  to="/industries"
                  data-testid={`home-industry-card-${i}`}
                  className="flex h-full flex-col rounded-xl border border-hairline bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-pine/40 hover:shadow-lg"
                >
                  <ind.icon size={22} className="text-pine" />
                  <h3 className="mt-4 font-serif text-xl font-semibold leading-snug text-ink">{ind.title}</h3>
                  <p className="mt-3 text-xs leading-relaxed text-stone2">{ind.sub}</p>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING TEASER */}
      <section className="bg-pine-950 py-24 text-cream lg:py-32" data-testid="home-pricing-teaser">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <FadeUp>
              <Overline light>Pricing · 2026</Overline>
            </FadeUp>
            <MaskedLines
              as="h2"
              inView
              lines={["What does bookkeeping", "cost in 2026?"]}
              className="mt-5 font-serif text-4xl font-medium leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl"
            />
            <FadeUp delay={0.2}>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-cream/70">
                Pricing is based on what your business actually needs — not a predetermined
                package filled with services you may never use.
              </p>
            </FadeUp>
            <FadeUp delay={0.3}>
              <div className="mt-8 flex flex-wrap gap-2.5">
                {FACTORS.map((f) => (
                  <span
                    key={f}
                    className="rounded-full border border-cream/20 px-4 py-2 font-mono text-[11px] tracking-wide text-cream/80"
                  >
                    {f}
                  </span>
                ))}
              </div>
            </FadeUp>
            <FadeUp delay={0.4}>
              <Link
                to="/pricing"
                data-testid="home-pricing-teaser-cta"
                className="mt-10 inline-flex items-center gap-2 rounded-md bg-gold px-7 py-4 text-sm font-semibold text-ink transition-transform duration-200 hover:-translate-y-0.5"
              >
                See how pricing works <ArrowRight size={15} />
              </Link>
            </FadeUp>
          </div>
          <div className="lg:col-span-5">
            <FadeUp delay={0.25}>
              <div className="relative rounded-2xl border border-cream/15 bg-pine-900/60 p-8 backdrop-blur">
                <LogoMark light size={48} />
                <p className="mt-6 font-serif text-2xl italic leading-snug text-cream/90">
                  "A business with one bank account and relatively few monthly transactions may
                  only need basic reconciliation and financial reporting. A growing company with
                  multiple accounts, payroll, sales tax, or job costing will naturally require
                  more involvement."
                </p>
                <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-gold">
                  Arnold Bookkeeping, LLC
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* FOUNDER SNIPPET */}
      <section className="py-24 lg:py-32" data-testid="home-founder-section">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <FadeUp>
              <div className="relative" data-testid="home-founder-portrait">
                <div className="absolute -inset-3 -rotate-2 rounded-2xl border border-gold/50" aria-hidden="true" />
                <img
                  src="https://customer-assets-jt897jd0.emergentagent.net/job_ledgerwise-20/artifacts/7eaf94c182494b01_1719691387521%20Headshot%20Photo.jpg"
                  alt="Aaron Arnold, founder of Arnold Bookkeeping"
                  className="relative aspect-[4/5] w-full rounded-2xl object-cover object-top shadow-2xl"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </FadeUp>
          </div>
          <div className="lg:col-span-8">
            <FadeUp>
              <Overline>Our story</Overline>
            </FadeUp>
            <MaskedLines
              as="h2"
              inView
              lines={["Built around a simple idea."]}
              className="mt-5 font-serif text-4xl font-medium leading-[1.08] tracking-tight text-ink sm:text-5xl"
            />
            <FadeUp delay={0.2}>
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-stone2">
                I'm a serial entrepreneur who evolved into a bookkeeper — years of owning
                businesses, managing rental properties, working in construction estimating, and
                more than two decades in the military. One thing remained consistent: good
                decisions depend on good information.
              </p>
            </FadeUp>
            <FadeUp delay={0.3}>
              <div className="mt-8 flex flex-wrap items-center gap-6">
                <Link
                  to="/our-story"
                  data-testid="home-founder-cta"
                  className="group inline-flex items-center gap-2 rounded-md bg-pine px-6 py-3.5 text-sm font-semibold text-cream transition-colors duration-200 hover:bg-gold hover:text-ink"
                >
                  Meet Aaron Arnold
                  <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
                <span className="font-serif text-3xl italic text-pine/70">— Aaron Arnold</span>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* LOCAL SERVICE AREA */}
      <section className="border-t border-hairline py-16" data-testid="home-local-section">
        <div className="mx-auto max-w-7xl px-6">
          <FadeUp>
            <Overline>Locally rooted</Overline>
            <div className="mt-5 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
              <p className="max-w-2xl font-serif text-2xl italic leading-snug text-ink sm:text-3xl">
                Proudly serving small businesses in Wellsville, PA and surrounding Central
                Pennsylvania communities.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/services"
                  data-testid="home-local-services-link"
                  className="inline-flex items-center gap-2 rounded-md bg-pine px-6 py-3.5 text-sm font-semibold text-cream transition-colors duration-200 hover:bg-gold hover:text-ink"
                >
                  Bookkeeping services
                </Link>
                <Link
                  to="/tax-services"
                  data-testid="home-local-tax-link"
                  className="inline-flex items-center gap-2 rounded-md border border-pine/25 px-6 py-3.5 text-sm font-semibold text-pine transition-colors duration-200 hover:border-pine"
                >
                  Tax prep services
                </Link>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
